import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  BarChart3,
  Zap,
  Loader2,
  Crown,
  Lock,
  ChevronRight,
  Sparkles,
  Target,
  Star,
  Shield,
  UserPlus,
  Check,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TROPHY_CONFIG = {
  pointsLeader: { label: 'Pts Leader', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  reboundsLeader: { label: 'Reb Leader', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  assistsLeader: { label: 'Ast Leader', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
};

export const GamePlayerStats = ({ gameId, gameState }) => {
  const { isAuthenticated, hasRequiredTier } = useAuth();
  const [loading, setLoading] = useState(true);
  const [playerStats, setPlayerStats] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [coachInsights, setCoachInsights] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [teams, setTeams] = useState([]);
  const [activeSection, setActiveSection] = useState('stats');
  const [gameClaims, setGameClaims] = useState([]);
  const { user } = useAuth();

  const hasEliteAccess = isAuthenticated() && hasRequiredTier('elite');

  const fetchGameStats = useCallback(async () => {
    if (!gameId) return;
    setLoading(true);
    try {
      const svGameId = gameState?.sportsvisio_game_id;
      if (svGameId) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 12000);
        const res = await fetch(`${API_URL}/api/sportsvisio/game-analytics/${svGameId}`, { signal: controller.signal });
        clearTimeout(timeout);
        if (res.ok) {
          const data = await res.json();
          setAiSummary(data.insights);
          setCoachInsights(data.coachInsights);
          setHighlight(data.highlight);
          setTeams(data.teams || []);
          if (data.playerStats?.length > 0) {
            setPlayerStats(data.playerStats);
          }
        }
      } else {
        const res = await fetch(`${API_URL}/api/sportsvisio/game/${gameId}/stats`);
        if (res.ok) {
          const raw = await res.json();
          setPlayerStats(Array.isArray(raw) ? raw : []);
        }
        const insightsRes = await fetch(`${API_URL}/api/sportsvisio/game/${gameId}/insights`);
        if (insightsRes.ok) {
          const insights = await insightsRes.json();
          setAiSummary(insights.aiSummary);
        }
      }
    } catch (err) {
      console.error('Failed to fetch game stats:', err);
    }
    setLoading(false);
  }, [gameId, gameState?.sportsvisio_game_id]);

  // Fetch game claims (which players have linked their profiles)
  const fetchClaims = useCallback(async () => {
    if (!gameId) return;
    try {
      const res = await fetch(`${API_URL}/api/games/${gameId}/claims`);
      if (res.ok) setGameClaims(await res.json());
    } catch {}
  }, [gameId]);

  useEffect(() => {
    if (gameState?.status === 'final' || gameState?.status === 'final-approved') {
      fetchGameStats();
      fetchClaims();
    } else {
      setLoading(false);
    }
  }, [gameId, gameState?.status, fetchGameStats, fetchClaims]);

  if (gameState?.status !== 'final' && gameState?.status !== 'final-approved') {
    return null;
  }

  const hasSVData = playerStats.length > 0 || aiSummary || coachInsights || highlight;

  const teamMap = {};
  for (const t of teams) teamMap[t.id] = t;

  return (
    <div className="space-y-0" data-testid="game-analytics-section">
      {/* Premium Partnership Banner */}
      {gameState?.sportsvisio_game_id && (
        <div className="flex items-center justify-center gap-3 py-3 px-4 bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 border border-white/5 rounded-t-xl" data-testid="sv-partnership-banner">
          <span className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-medium">Advanced Analytics by</span>
          <img src="/sportsvisio-logo.png" alt="SportsVisio" className="h-5 opacity-90" />
        </div>
      )}

      <Card className={`bg-[#0a0a0a] border-white/5 ${gameState?.sportsvisio_game_id ? 'rounded-t-none border-t-0' : ''}`}>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 text-white/30 animate-spin" />
            </div>
          ) : !hasSVData ? (
            <div className="text-center py-10">
              <BarChart3 className="h-8 w-8 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-600 text-sm">Analytics not yet available</p>
              <p className="text-zinc-700 text-xs mt-1">Data appears after AI processing completes</p>
            </div>
          ) : !hasEliteAccess ? (
            /* Free/Basic/Plus — show box score but gate the rest */
            <div>
              {/* Box Score visible to all */}
              {playerStats.length > 0 && (
                <div className="p-5">
                  <PremiumBoxScore
                    stats={playerStats}
                    teams={teams}
                    gameClaims={gameClaims}
                    gameId={gameId}
                    gameState={gameState}
                    isAuthenticated={isAuthenticated}
                    hasEliteAccess={false}
                    user={user}
                    onClaimSuccess={fetchClaims}
                  />
                </div>
              )}
              {/* Upgrade CTA for the rest */}
              <div className="py-6 px-6 border-t border-white/5">
                <div className="text-center mb-4">
                  <Crown className="h-7 w-7 text-amber-400 mx-auto mb-2" />
                  <h4 className="text-white font-semibold mb-1">Unlock Full Analytics</h4>
                  <p className="text-zinc-500 text-sm max-w-md mx-auto">
                    Upgrade to Rooam Elite for AI breakdowns, coach reports, highlight reels, and the ability to claim your stats.
                  </p>
                </div>
                <div className="grid grid-cols-4 gap-2 mb-4 max-w-md mx-auto">
                  {[
                    { icon: Sparkles, label: 'AI Breakdown' },
                    { icon: Target, label: 'Coach Report' },
                    { icon: Star, label: 'Highlights' },
                    { icon: UserPlus, label: 'Claim Stats' },
                  ].map(({ icon: Icon, label }) => (
                    <div key={label} className="bg-white/3 border border-white/5 rounded-lg p-2.5 text-center">
                      <Lock className="h-3.5 w-3.5 text-zinc-700 mx-auto mb-1" />
                      <span className="text-[9px] text-zinc-600">{label}</span>
                    </div>
                  ))}
                </div>
                {aiSummary && (
                  <div className="bg-white/2 border border-white/5 rounded-lg p-3 mb-4 mx-auto max-w-md relative overflow-hidden">
                    <p className="text-[9px] text-red-500 font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> AI Summary Preview
                    </p>
                    <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">{aiSummary}</p>
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                  </div>
                )}
                <div className="text-center">
                  <Button className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm px-6" asChild>
                    <Link to="/membership">
                      <Crown className="h-4 w-4 mr-1.5" />
                      Upgrade to Elite
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* Full Premium Stats View */
            <div>
              {/* Section Tabs */}
              <div className="flex border-b border-white/5">
                {[
                  ...(aiSummary ? [{ key: 'summary', label: 'AI Breakdown' }] : []),
                  ...(playerStats.length > 0 ? [{ key: 'stats', label: 'Box Score' }] : []),
                  ...(coachInsights?.length > 0 ? [{ key: 'coach', label: 'Coach Report' }] : []),
                  ...(highlight ? [{ key: 'highlight', label: 'Highlights' }] : []),
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setActiveSection(key)}
                    data-testid={`analytics-tab-${key}`}
                    className={`flex-1 py-3 text-xs font-medium tracking-wide uppercase transition-all ${
                      activeSection === key
                        ? 'text-white border-b-2 border-white'
                        : 'text-zinc-600 hover:text-zinc-400'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="p-5">
                {/* AI Summary */}
                {activeSection === 'summary' && aiSummary && (
                  <div data-testid="analytics-summary">
                    {aiSummary.split('\n').filter(p => p.trim()).map((p, i) => (
                      <p key={i} className="text-sm text-zinc-300 leading-[1.8] mb-3">{p}</p>
                    ))}
                  </div>
                )}

                {/* Box Score */}
                {activeSection === 'stats' && playerStats.length > 0 && (
                  <PremiumBoxScore
                    stats={playerStats}
                    teams={teams}
                    gameClaims={gameClaims}
                    gameId={gameId}
                    gameState={gameState}
                    isAuthenticated={isAuthenticated}
                    hasEliteAccess={hasEliteAccess}
                    user={user}
                    onClaimSuccess={fetchClaims}
                  />
                )}

                {/* Coach Report */}
                {activeSection === 'coach' && coachInsights?.length > 0 && (
                  <div className="space-y-6" data-testid="analytics-coach">
                    {coachInsights.map((ci, idx) => {
                      const teamName = teamMap[ci.teamId]?.name || `Team ${idx + 1}`;
                      return (
                        <div key={ci.teamId || idx}>
                          <div className="flex items-center gap-2 mb-3">
                            <Shield className="h-4 w-4 text-zinc-400" />
                            <span className="text-sm font-semibold text-white uppercase tracking-wide">{teamName}</span>
                          </div>
                          {ci.positive && (
                            <div className="mb-3 p-4 bg-emerald-950/15 border-l-2 border-emerald-500 rounded-r-lg">
                              <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider mb-2">Strengths</p>
                              <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{ci.positive.replace(/\*\*/g, '')}</p>
                            </div>
                          )}
                          {ci.negative && (
                            <div className="p-4 bg-red-950/15 border-l-2 border-red-500 rounded-r-lg">
                              <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-2">Areas to Improve</p>
                              <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{ci.negative.replace(/\*\*/g, '')}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Highlight Reel */}
                {activeSection === 'highlight' && highlight && (
                  <div data-testid="analytics-highlight">
                    <div className="rounded-lg overflow-hidden bg-black aspect-video">
                      <video
                        src={highlight.url}
                        poster={highlight.thumbnailUrl}
                        controls
                        className="w-full h-full object-contain"
                        preload="metadata"
                      />
                    </div>
                    {highlight.duration && (
                      <p className="text-[10px] text-zinc-600 text-center mt-2 uppercase tracking-wider">
                        {Math.round(highlight.duration)}s Highlight Reel
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* Premium Box Score — clean ESPN-style with claim buttons */
const PremiumBoxScore = ({ stats, teams, gameClaims, gameId, gameState, isAuthenticated, hasEliteAccess, user, onClaimSuccess }) => {
  const navigate = useNavigate();
  const teamGroups = {};
  for (const t of teams) teamGroups[t.id] = { ...t, players: [] };

  for (const p of stats) {
    const g = teamGroups[p.teamId];
    if (g) g.players.push(p);
  }

  for (const g of Object.values(teamGroups)) {
    g.players.sort((a, b) => (b.points || 0) - (a.points || 0));
  }

  // Build claim lookup: { "teamName-jerseyNumber" => claim }
  const claimMap = {};
  for (const c of gameClaims || []) {
    claimMap[`${c.team_name.toLowerCase()}-${c.jersey_number}`] = c;
  }

  const handleClaim = async (teamName, jerseyNumber) => {
    if (!isAuthenticated()) {
      toast.error('Sign in with a Rooam Elite account to claim your stats');
      navigate('/login');
      return;
    }
    if (!hasEliteAccess) {
      toast.error('Rooam Elite membership required to claim game stats');
      navigate('/membership');
      return;
    }
    // Check if user has an athlete profile
    try {
      const res = await fetch(`${API_URL}/api/athletes/me`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('rooam_token') || localStorage.getItem('rooam_token')}` }
      });
      if (!res.ok) {
        toast.error('Create an athlete profile first to claim game spots');
        navigate('/athletes');
        return;
      }
      const athlete = await res.json();
      // Make the claim
      const claimRes = await fetch(`${API_URL}/api/athletes/${athlete.id}/game-claims`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('rooam_token') || localStorage.getItem('rooam_token')}`
        },
        body: JSON.stringify({
          game_id: gameId,
          sportsvisio_game_id: gameState?.sportsvisio_game_id || null,
          team_name: teamName,
          jersey_number: jerseyNumber,
        })
      });
      if (claimRes.ok) {
        toast.success(`Claimed #${jerseyNumber} on ${teamName}!`);
        onClaimSuccess?.();
      } else {
        const err = await claimRes.json();
        toast.error(err.detail || 'Failed to claim');
      }
    } catch {
      toast.error('Something went wrong');
    }
  };

  return (
    <div className="space-y-6" data-testid="analytics-boxscore">
      {Object.entries(teamGroups).map(([tid, group]) => (
        <div key={tid}>
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/5">
            <span className="text-xs font-bold text-white uppercase tracking-wider">{group.name}</span>
            <span className="text-[10px] text-zinc-600">{group.score} PTS</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-[10px] text-zinc-600 uppercase tracking-wider">
                  <th className="text-left py-2 pr-2 font-medium w-8">#</th>
                  <th className="text-left py-2 pr-4 font-medium">Player</th>
                  <th className="text-center py-2 px-2 font-medium">PTS</th>
                  <th className="text-center py-2 px-2 font-medium">REB</th>
                  <th className="text-center py-2 px-2 font-medium">AST</th>
                  <th className="text-center py-2 px-2 font-medium">STL</th>
                  <th className="text-center py-2 px-2 font-medium">BLK</th>
                  <th className="text-center py-2 px-2 font-medium">TO</th>
                  <th className="text-center py-2 px-2 font-medium">FG</th>
                  <th className="text-center py-2 px-2 font-medium">3PT</th>
                  <th className="text-center py-2 px-2 font-medium">FT</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {group.players.map((p, i) => {
                  const fg = p.fieldGoals || {};
                  const tp = p.threePoints || {};
                  const ft = p.freeThrows || {};
                  const isTopScorer = i === 0;
                  const claimKey = `${group.name.toLowerCase()}-${p.number}`;
                  const claim = claimMap[claimKey];
                  const isClaimed = !!claim;

                  const rowContent = (
                    <>
                      <td className="py-2.5 pr-2 text-zinc-500 font-mono text-[11px]">{p.number || '-'}</td>
                      <td className="py-2.5 pr-4">
                        <span className={`font-medium ${isClaimed ? 'text-cyan-400' : isTopScorer ? 'text-white' : 'text-zinc-300'}`}>
                          {isClaimed ? claim.athlete_name : (p.name || `#${p.number}`)}
                        </span>
                        {isClaimed && (
                          <Badge className="ml-1.5 bg-cyan-500/10 text-cyan-500 border-cyan-500/20 text-[8px] px-1 py-0">Verified</Badge>
                        )}
                      </td>
                      <td className={`text-center py-2.5 px-2 font-bold tabular-nums ${p.points >= 15 ? 'text-white' : p.points >= 10 ? 'text-zinc-200' : 'text-zinc-400'}`}>{p.points}</td>
                      <td className={`text-center py-2.5 px-2 tabular-nums ${p.rebounds >= 8 ? 'text-white' : 'text-zinc-500'}`}>{p.rebounds}</td>
                      <td className={`text-center py-2.5 px-2 tabular-nums ${p.assists >= 5 ? 'text-white' : 'text-zinc-500'}`}>{p.assists}</td>
                      <td className="text-center py-2.5 px-2 tabular-nums text-zinc-500">{p.steals}</td>
                      <td className="text-center py-2.5 px-2 tabular-nums text-zinc-500">{p.blocks}</td>
                      <td className="text-center py-2.5 px-2 tabular-nums text-zinc-600">{p.turnovers}</td>
                      <td className="text-center py-2.5 px-2 tabular-nums text-zinc-500">{fg.made || 0}-{fg.attempts || 0}</td>
                      <td className="text-center py-2.5 px-2 tabular-nums text-zinc-500">{tp.made || 0}-{tp.attempts || 0}</td>
                      <td className="text-center py-2.5 px-2 tabular-nums text-zinc-500">{ft.made || 0}-{ft.attempts || 0}</td>
                      <td className="text-right py-2.5 pl-2">
                        {isClaimed ? (
                          <ChevronRight className="h-3.5 w-3.5 text-cyan-500 inline" />
                        ) : hasEliteAccess ? (
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClaim(group.name, p.number); }}
                            className="text-[9px] text-zinc-600 hover:text-cyan-400 transition-colors flex items-center gap-0.5"
                            data-testid={`claim-btn-${group.name}-${p.number}`}
                          >
                            <UserPlus className="h-3 w-3" />
                            <span className="hidden sm:inline">Claim</span>
                          </button>
                        ) : (
                          <Lock className="h-3 w-3 text-zinc-800 inline" />
                        )}
                      </td>
                    </>
                  );

                  if (isClaimed) {
                    return (
                      <tr
                        key={p.playerId || i}
                        className={`border-t border-white/3 text-xs cursor-pointer hover:bg-cyan-500/5 ${isTopScorer ? 'bg-white/2' : ''}`}
                        onClick={() => navigate(`/athlete/${claim.athlete_id}`)}
                        data-testid={`player-row-${p.number}`}
                      >
                        {rowContent}
                      </tr>
                    );
                  }

                  return (
                    <tr
                      key={p.playerId || i}
                      className={`border-t border-white/3 text-xs ${isTopScorer ? 'bg-white/2' : 'hover:bg-white/2'}`}
                      data-testid={`player-row-${p.number}`}
                    >
                      {rowContent}
                    </tr>
                  );
                })}
                {/* Team Totals */}
                <tr className="border-t border-white/10 text-xs font-semibold">
                  <td className="py-2.5 pr-2"></td>
                  <td className="py-2.5 pr-4 text-zinc-400 uppercase text-[10px] tracking-wider">Totals</td>
                  <td className="text-center py-2.5 px-2 text-white tabular-nums">{group.players.reduce((s, p) => s + (p.points || 0), 0)}</td>
                  <td className="text-center py-2.5 px-2 text-zinc-400 tabular-nums">{group.players.reduce((s, p) => s + (p.rebounds || 0), 0)}</td>
                  <td className="text-center py-2.5 px-2 text-zinc-400 tabular-nums">{group.players.reduce((s, p) => s + (p.assists || 0), 0)}</td>
                  <td className="text-center py-2.5 px-2 text-zinc-400 tabular-nums">{group.players.reduce((s, p) => s + (p.steals || 0), 0)}</td>
                  <td className="text-center py-2.5 px-2 text-zinc-400 tabular-nums">{group.players.reduce((s, p) => s + (p.blocks || 0), 0)}</td>
                  <td className="text-center py-2.5 px-2 text-zinc-600 tabular-nums">{group.players.reduce((s, p) => s + (p.turnovers || 0), 0)}</td>
                  <td colSpan={4}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GamePlayerStats;
